import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import Application from '../Application';
import LoadingScreen from './components/LoadingScreen';
import MuteToggle from './components/MuteToggle';
import WelcomeOverlay from './components/WelcomeOverlay';
import CameraToolbar from './components/CameraToolbar';

const MIN_LOADING_MS = 900;

const UIRoot: React.FC = () => {
    const [progress, setProgress] = useState(0);
    const [displayProgress, setDisplayProgress] = useState(0);
    const [ready, setReady] = useState(false);
    const [welcomeOpen, setWelcomeOpen] = useState(false);
    const progressRef = useRef(0);
    const loadStartedAtRef = useRef(Date.now());
    const readyHandledRef = useRef(false);

    useEffect(() => {
        const app = new Application();
        const onProgress = (...args: unknown[]) => {
            const next = Math.max(0, Math.min(1, args[0] as number));
            progressRef.current = next;
            setProgress(next);
        };
        const onReady = () => {
            if (readyHandledRef.current) return;
            readyHandledRef.current = true;
            progressRef.current = 1;
            setProgress(1);
            setDisplayProgress(1);
            const elapsed = Date.now() - loadStartedAtRef.current;
            window.setTimeout(() => {
                setReady(true);
                setWelcomeOpen(true);
            }, Math.max(120, MIN_LOADING_MS - elapsed));
        };
        app.loading.on('progress', onProgress);
        app.loading.on('ready', onReady);
        if (app.loading.progress > 0) {
            onProgress(app.loading.progress);
        }
        if (app.loading.finished) {
            onReady();
        }
        return () => {
            app.loading.off('progress', onProgress);
            app.loading.off('ready', onReady);
        };
    }, []);

    useEffect(() => {
        if (ready) return;

        const timer = window.setInterval(() => {
            const elapsedRatio = Math.min(1, (Date.now() - loadStartedAtRef.current) / MIN_LOADING_MS);
            const realProgress = progressRef.current;
            const pacedProgress = realProgress >= 1 ? 1 : Math.min(0.96, elapsedRatio * 0.96);
            setDisplayProgress(Math.max(pacedProgress, realProgress >= 1 ? 1 : Math.min(realProgress, 0.92)));
        }, 80);

        return () => window.clearInterval(timer);
    }, [ready, progress]);

    return (
        <>
            {!ready && <LoadingScreen progress={displayProgress} />}
            {ready && welcomeOpen && (
                <WelcomeOverlay onDismiss={() => setWelcomeOpen(false)} />
            )}
            {ready && <CameraToolbar />}
            {ready && <MuteToggle />}
        </>
    );
};

export default class UI {
    constructor() {
        const root = document.getElementById('ui');
        if (!root) return;
        ReactDOM.render(<UIRoot />, root);
    }
}
